<template>
    <Head title="Session error repro" />
    <div class="mx-auto flex max-w-3xl flex-col gap-10 p-10">
        <header class="flex flex-col gap-2">
            <h1 class="title">Session error repro</h1>
            <p class="body text-ink">
                Produces the two states <code>csrf_failure_view</code> tells apart, so the session-ended dialog can be exercised in one browser instead of two magic links and an
                incognito window. Development and staging only.
            </p>
        </header>

        <section class="flex flex-col gap-3 rounded-xl border-2 border-border p-6">
            <h2 class="title-section">Stale CSRF token — “That didn’t go through”</h2>
            <p class="body-small text-ink">
                Opens the homepage, then logs you in again in a second tab. The re-login rotates the session’s CSRF secret, so the first tab is still signed in but holding a dead
                token. Switch back to it and send a chat message.
            </p>
            <div class="flex flex-wrap gap-3">
                <BaseButton theme="primary" data-testid="repro-stale-token" @click="reproStaleToken">Open both tabs</BaseButton>
                <BaseButton theme="secondary" @click="openHomeTab">Homepage tab only</BaseButton>
                <BaseButton theme="secondary" @click="openReloginTab">Re-login tab only</BaseButton>
            </div>
        </section>

        <section class="flex flex-col gap-3 rounded-xl border-2 border-border p-6">
            <h2 class="title-section">Dead session — “Your session has expired”</h2>
            <p class="body-small text-ink">
                Opens the homepage, then destroys the session. There is one session per browser, so this signs
                <strong>this</strong> tab out too — that is the point, and the reason the state is awkward to produce by hand.
            </p>
            <div class="flex flex-wrap items-center gap-3">
                <BaseButton theme="danger" data-testid="repro-dead-session" @click="reproDeadSession">Open a tab, then destroy the session</BaseButton>
                <!-- A plain anchor, not BaseLink: an Inertia visit on a destroyed session is
                     the very failure this page just produced. -->
                <a v-if="isExpired" class="base-link body-small" :href="login_url">Log back in</a>
            </div>
        </section>

        <section class="flex flex-col gap-3">
            <div class="flex items-center gap-3">
                <h2 class="title-section">Session state</h2>
                <BaseButton theme="tertiary" size="sm" @click="refreshState">Refresh</BaseButton>
            </div>
            <dl class="body-small grid grid-cols-[auto_1fr] gap-x-6 gap-y-1" data-testid="session-state">
                <dt>Identifies a user</dt>
                <dd>{{ state.session_has_auth }}</dd>
                <dt>Holds a CSRF secret</dt>
                <dd>{{ state.session_has_csrf_secret }}</dd>
                <dt>Expires at</dt>
                <dd>{{ state.session_expires_at ?? "not set" }}</dd>
            </dl>
        </section>
    </div>
</template>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
/**
 * Staff tooling for the session-ended dialog (ENG-1667). Pairs with vpoc/dev_session_views.py,
 * which gates every endpoint behind an environment allow-list — this page is only ever reachable
 * where those views are.
 */
import BaseButton from "~vue/components/BaseButton.vue";
import { refreshCsrfToken } from "~vue/utils/csrf";
import { ref } from "vue";

import { getCookie } from "../../utils.js";

type SessionState = {
    session_has_auth: boolean;
    session_has_csrf_secret: boolean;
    session_expires_at: string | null;
};

const props = defineProps<{
    home_url: string;
    relogin_url: string;
    expire_url: string;
    state_url: string;
    login_url: string;
    session_state: SessionState;
}>();

/**
 * How long the tab being reproduced against gets to render before the session changes under it.
 * Any shorter and it can still be loading, in which case it simply lands on the login page and
 * there is nothing left to reproduce.
 */
const TAB_RENDER_MS = 1500;

const state = ref<SessionState>(props.session_state);
const isExpired = ref(false);

function openHomeTab(): Window | null {
    return globalThis.open(props.home_url, "_blank");
}

function openReloginTab(): Window | null {
    return globalThis.open(props.relogin_url, "_blank");
}

/**
 * Both tabs are opened during the click itself, because a `window.open` deferred past the user
 * gesture is what popup blockers exist to stop. The second one starts blank and is navigated on
 * a timer, which is what actually orders the two.
 */
function reproStaleToken(): void {
    openHomeTab();
    const reloginTab = globalThis.open("", "_blank");
    setTimeout(() => {
        if (reloginTab) {
            reloginTab.location.href = props.relogin_url;
        }
    }, TAB_RENDER_MS);
}

async function post(url: string): Promise<Response> {
    const send = () =>
        fetch(url, {
            method: "POST",
            credentials: "same-origin",
            headers: { "X-CSRFToken": getCookie("csrftoken") ?? "" },
        });
    const response = await send();
    // This page's own token goes stale as soon as the re-login tool above is used, which would
    // otherwise leave the second tool permanently 403ing.
    if (response.status === 403 && (await refreshCsrfToken())) {
        return send();
    }
    return response;
}

async function reproDeadSession(): Promise<void> {
    openHomeTab();
    await new Promise((resolve) => setTimeout(resolve, TAB_RENDER_MS));
    const response = await post(props.expire_url);
    isExpired.value = response.ok;
    await refreshState();
}

async function refreshState(): Promise<void> {
    const response = await fetch(props.state_url, { credentials: "same-origin" });
    if (response.ok) {
        state.value = (await response.json()) as SessionState;
    }
}
</script>
